import { Config } from "@kaltura/mediaspace-shared-data-kms-config";
import { HeaderMenu, HeaderMenuProps } from "./header";
import { Footer } from "./footer/Footer";
import { SidebarContentsTitle, SidebarMenuButton, SidebarMenuButtonProps } from "@kaltura/ds-react-layouts";
import { Chat24Icon } from "@kaltura/ds-react-icons";
import { Box, useTheme } from "@kaltura/mediaspace-shared-styled";
import { mockContext } from "@kaltura/mediaspace-storybook";
import { DsLayout } from "./DsLayout";
import { ComponentType, useMemo } from "react";
import { Decorator } from "@storybook/react";
import deepmerge from "deepmerge";

type DeepPartial<T> = T extends object ? { [K in keyof T]?: DeepPartial<T[K]> } : T;
export type ConfigOverrides = DeepPartial<Config>;

interface MockDsLayoutConfigOptions {
    mode?: "dark" | "light";
    headerColor?: "dark" | "light" | "transparent";
    headerProps?: HeaderMenuProps;
    overrides?: ConfigOverrides;
}

const MockSidebarContents = () => (
    <div>
        <SidebarContentsTitle title={"Private chat"} icon={<Chat24Icon />} />
        <Box p={2}>Shhh! It's private!</Box>
    </div>
);

const sidebarMenuButtonProps: SidebarMenuButtonProps = {
    title: "Private chat",
    icon: <Chat24Icon />,
};

/**
 * DS layout config for storybook.
 *
 * Usage:
 * ```
 * <DsLayout
 *     context={getMockDsLayoutConfig()}
 *     component={() => <>...</>}
 * />
 * ```
 */
export const getMockDsLayoutConfig = ({
    mode = "light",
    headerColor,
    headerProps,
    overrides = {},
}: MockDsLayoutConfigOptions): Config =>
    deepmerge(
        {
            ...mockContext,
            theming: {
                mode,
            },
            dsLayout: {
                components: {
                    header:
                        headerColor || headerProps
                            ? [
                                  {
                                      name: "Header",
                                      component: HeaderMenu,
                                      props: headerProps ?? {
                                          headerColor,
                                          logo: {
                                              hasLogo: false,
                                          },
                                          showNav: true,
                                          scrollThreshold: 60,
                                      },
                                  },
                              ]
                            : [],
                    footer: [
                        {
                            name: "Footer",
                            component: Footer,
                            props: {
                                backgroundColor: "#2E2E2E",
                                backgroundUrl: "",
                                textAndLinks: [
                                    {
                                        sectionText: "Corp Site",
                                        sectionType: "URL",
                                        sectionLink: "https://corp.kaltura.com",
                                    },
                                    {
                                        sectionText: "Pricing",
                                        sectionType: "URL",
                                        sectionLink: "https://corp.kaltura.com/pricing",
                                    },
                                    {
                                        sectionText: "Learning",
                                        sectionType: "URL",
                                        sectionLink: "https://learning.kaltura.com",
                                    },
                                    {
                                        sectionText: "Script",
                                        sectionType: "Script",
                                        sectionLink: "alert('hi!!!');",
                                    },
                                    {
                                        sectionText: "Empty Script",
                                        sectionType: "Script",
                                        sectionLink: "",
                                    },
                                ],
                                linkSeparator: "\u2022",
                                bottomText: "Kaltura, Inc. or its affiliates, All Rights Reserved",
                            },
                        },
                    ],
                },
                sidebarTopButtons: [
                    {
                        buttonDescriptor: {
                            id: "cnc",
                            buttonComponent: SidebarMenuButton,
                            buttonProps: sidebarMenuButtonProps,
                            contentsComponent: MockSidebarContents,
                        },
                        contentsProps: {},
                    },
                ],
                headerColor,
            },
        },
        overrides as Config
    );

interface MockDsLayoutProps<PropsT extends object> extends Omit<MockDsLayoutConfigOptions, "mode"> {
    component: ComponentType<PropsT>;
    props: PropsT;
}

export const MockDsLayout = <PropsT extends object>({
    headerColor,
    headerProps,
    overrides,
    component,
    props,
}: MockDsLayoutProps<PropsT>) => {
    const {
        kaltura: { mode },
    } = useTheme();

    const config = useMemo(
        () => getMockDsLayoutConfig({ headerColor, headerProps, overrides, mode }),
        [headerColor, headerProps, overrides, mode]
    );

    return <DsLayout context={config} component={component} props={props} />;
};

export const withMockDsLayout =
    (options: Omit<MockDsLayoutConfigOptions, "mode">): Decorator =>
    (Story) => <MockDsLayout {...options} component={Story} props={{}} />;
